import {useEffect, useMemo, useRef, useState} from "react";

import {useAppDispatch} from "../../store/hooks";
import {useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {usePatchUserProfileSilentlyMutation} from "../../user/api/patch_user_profile";
import {usePostReservationConfirmMutation, usePostReservationInitiateMutation} from "../api/reservation_api";
import {getReservationEntry} from "../constants/reservation_placement_config";
import {ReservationConfirmedVariant} from "../types/IReservationConfirmedStepContentProps";
import {IReservationCodeEntryActions, IReservationFlowEngine, IReservationFlowLatestParams} from "../types/IReservationFlowEngine";
import {IReservationRequest} from "../types/IReservationRequest";
import {IUseReservationFlowParams, IUseReservationFlowResult} from "../types/IUseReservationFlow";
import {ReservationEntry} from "../types/ReservationEntry";
import {ReservationFlowState} from "../types/ReservationFlowState";
import {createReservationFlowEngine, createReservationFlowRun} from "../utils/create_reservation_flow_engine";
import {createReservationFlowTransitions} from "../utils/create_reservation_flow_transitions";
import {getReservationCodeStepView, isProfileNameFailure, isReservationRequestPending} from "../utils/get_reservation_code_step_view";
import {maskEmail} from "../utils/mask_email";
import {createEmptyReservationDigits} from "../utils/reservation_code_digits";
import {reservationFlowInitialState} from "../utils/reservation_flow_reducer";
import {useRefState} from "./use_ref_state";
import {useHandleExpiryTimer, useReservationClock} from "./use_reservation_clock";
import {useReservationFlowTracking} from "./use_reservation_flow_tracking";
import {IReservationNameFormState, useReservationNameForm} from "./use_reservation_name_form";

// the unlock choice modal confirms with its own screen, also once an expired session has moved its request to the reservation modal
const CONFIRMED_VARIANTS: Record<ReservationEntry, ReservationConfirmedVariant> = {
    cta: "submitted",
    unlock_choice: "reservation_24h"
};

interface IReservationCodeEntry {
    digits: string[];
    focusRequestId: number;
    // set once a resent code is on its way - cleared by the next call, a reset or hiding the steps
    isNewCodeSent: boolean;
    actions: IReservationCodeEntryActions;
}

interface IReservationFlowResultParams {
    request: IReservationRequest | null;
    state: ReservationFlowState;
    now: number;
    userProfile: ReturnType<typeof useGetUserProfileQuery>["data"];
    codeEntry: IReservationCodeEntry;
    nameForm: IReservationNameFormState;
    engine: IReservationFlowEngine;
}

const useReservationCodeEntry = (isVisible: boolean): IReservationCodeEntry => {
    const [digits, setDigits] = useState(createEmptyReservationDigits);
    const [focusRequestId, setFocusRequestId] = useState(0);
    const [isNewCodeSent, setIsNewCodeSent] = useState(false);
    const actions = useMemo(() => ({setDigits, setIsNewCodeSent, requestFocus: () => setFocusRequestId((id) => id + 1)}), []);

    // the hidden steps take their live region with them - shown again, they announce nothing until another code is resent
    useEffect(() => {
        if (!isVisible) {
            setIsNewCodeSent(false);
        }
    }, [isVisible]);

    return {digits, focusRequestId, isNewCodeSent, actions};
};

const useIsMountedRef = () => {
    const isMountedRef = useRef(false);

    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    return isMountedRef;
};

const getReservationFlowResult = (params: IReservationFlowResultParams): IUseReservationFlowResult => {
    const {request, state, now, userProfile, codeEntry, nameForm, engine} = params;
    const isSubmitting = isReservationRequestPending(state) || nameForm.isSaving;
    const propertyDetails = {
        propertyNumber: request?.propertyNumber ?? null,
        offerType: request?.offerType ?? null,
        vendorName: request?.vendorName ?? null
    };

    return {
        state,
        isSubmitting,
        codeStepProps: {
            ...propertyDetails,
            ...getReservationCodeStepView(state, now),
            maskedEmail: maskEmail(userProfile?.email),
            digits: codeEntry.digits,
            focusRequestId: codeEntry.focusRequestId,
            isNewCodeSent: codeEntry.isNewCodeSent,
            isSubmitting,
            nameForm: isProfileNameFailure(state)
                ? {
                      initialName: nameForm.savedName ?? userProfile?.name ?? "",
                      isSaving: nameForm.isSaving,
                      errorMessage: nameForm.errorMessage,
                      onSubmit: engine.submitName
                  }
                : null,
            onDigitsChange: engine.setDigits,
            onComplete: engine.submitCode,
            onResend: engine.resend,
            onRetry: engine.retry
        },
        confirmedStepProps: {
            ...propertyDetails,
            variant: CONFIRMED_VARIANTS[request ? getReservationEntry(request) : "cta"]
        },
        start: engine.start,
        reset: engine.reset
    };
};

export const useReservationFlow = (params: IUseReservationFlowParams): IUseReservationFlowResult => {
    const {request, authPlacement, amplitudeLocation, autoStart = false, isVisible = true, onUnauthenticated} = params;

    const dispatch = useAppDispatch();
    const [postInitiate] = usePostReservationInitiateMutation();
    const [postConfirm] = usePostReservationConfirmMutation();
    const [patchUserProfile] = usePatchUserProfileSilentlyMutation();
    const {data: userProfile} = useGetUserProfileQuery();

    const [state, stateRef, setState] = useRefState(reservationFlowInitialState);
    const codeEntry = useReservationCodeEntry(isVisible);
    const [nameForm, nameFormActions] = useReservationNameForm();
    const tracking = useReservationFlowTracking({request, state, isVisible, authPlacement, amplitudeLocation});
    const clock = useReservationClock(state);
    const {refresh: refreshClock} = clock;
    const {actions: codeEntryActions} = codeEntry;

    const runRef = useRef(createReservationFlowRun());
    const latestRef = useRef<IReservationFlowLatestParams>({request, onUnauthenticated, postInitiate, postConfirm, patchUserProfile});
    const isMountedRef = useIsMountedRef();

    useEffect(() => {
        latestRef.current = {request, onUnauthenticated, postInitiate, postConfirm, patchUserProfile};
    });

    // Created once - it reads params through refs and uses stable setters only
    const engine = useMemo(() => {
        const transitions = createReservationFlowTransitions({stateRef, setState, refreshClock, setIsNewCodeSent: codeEntryActions.setIsNewCodeSent});

        return createReservationFlowEngine({
            dispatch,
            stateRef,
            runRef,
            latestRef,
            isMountedRef,
            transitions,
            codeEntry: codeEntryActions,
            tracking,
            nameFormActions
        });
    }, [dispatch, stateRef, setState, refreshClock, codeEntryActions, isMountedRef, tracking, nameFormActions]);

    const requestPropertyId = request?.propertyId ?? null;

    useEffect(() => {
        if (autoStart && requestPropertyId !== null) {
            engine.start();
        }
    }, [autoStart, requestPropertyId, engine]);

    useHandleExpiryTimer(state, engine.expireHandle);

    return getReservationFlowResult({request, state, now: clock.now, userProfile, codeEntry, nameForm, engine});
};
import {useEffect, useMemo, useRef, useState} from "react";

import {useAppDispatch} from "../../store/hooks";
import {useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {usePatchUserProfileSilentlyMutation} from "../../user/api/patch_user_profile";
import {usePostReservationConfirmMutation, usePostReservationInitiateMutation} from "../api/reservation_api";
import {getReservationEntry} from "../constants/reservation_placement_config";
import {ReservationConfirmedVariant} from "../types/IReservationConfirmedStepContentProps";
import {IReservationCodeEntryActions, IReservationFlowEngine, IReservationFlowLatestParams} from "../types/IReservationFlowEngine";
import {IReservationRequest} from "../types/IReservationRequest";
import {IUseReservationFlowParams, IUseReservationFlowResult} from "../types/IUseReservationFlow";
import {ReservationEntry} from "../types/ReservationEntry";
import {ReservationFlowState} from "../types/ReservationFlowState";
import {createReservationFlowEngine, createReservationFlowRun} from "../utils/create_reservation_flow_engine";
import {createReservationFlowTransitions} from "../utils/create_reservation_flow_transitions";
import {getReservationCodeStepView, isProfileNameFailure, isReservationRequestPending} from "../utils/get_reservation_code_step_view";
import {maskEmail} from "../utils/mask_email";
import {createEmptyReservationDigits} from "../utils/reservation_code_digits";
import {reservationFlowInitialState} from "../utils/reservation_flow_reducer";
import {useRefState} from "./use_ref_state";
import {useHandleExpiryTimer, useReservationClock} from "./use_reservation_clock";
import {useReservationFlowTracking} from "./use_reservation_flow_tracking";
import {IReservationNameFormState, useReservationNameForm} from "./use_reservation_name_form";

// the unlock choice modal confirms with its own screen, also once an expired session has moved its request to the reservation modal
const CONFIRMED_VARIANTS: Record<ReservationEntry, ReservationConfirmedVariant> = {
    cta: "submitted",
    unlock_choice: "reservation_24h"
};

interface IReservationCodeEntry {
    digits: string[];
    focusRequestId: number;
    // set once a resent code is on its way - cleared by the next call, a reset or hiding the steps
    isNewCodeSent: boolean;
    actions: IReservationCodeEntryActions;
}

interface IReservationFlowResultParams {
    request: IReservationRequest | null;
    state: ReservationFlowState;
    now: number;
    userProfile: ReturnType<typeof useGetUserProfileQuery>["data"];
    codeEntry: IReservationCodeEntry;
    nameForm: IReservationNameFormState;
    engine: IReservationFlowEngine;
}

const useReservationCodeEntry = (isVisible: boolean): IReservationCodeEntry => {
    const [digits, setDigits] = useState(createEmptyReservationDigits);
    const [focusRequestId, setFocusRequestId] = useState(0);
    const [isNewCodeSent, setIsNewCodeSent] = useState(false);
    const actions = useMemo(() => ({setDigits, setIsNewCodeSent, requestFocus: () => setFocusRequestId((id) => id + 1)}), []);

    // the hidden steps take their live region with them - shown again, they announce nothing until another code is resent
    useEffect(() => {
        if (!isVisible) {
            setIsNewCodeSent(false);
        }
    }, [isVisible]);

    return {digits, focusRequestId, isNewCodeSent, actions};
};

const useIsMountedRef = () => {
    const isMountedRef = useRef(false);

    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    return isMountedRef;
};

const getReservationFlowResult = (params: IReservationFlowResultParams): IUseReservationFlowResult => {
    const {request, state, now, userProfile, codeEntry, nameForm, engine} = params;
    const isSubmitting = isReservationRequestPending(state) || nameForm.isSaving;
    const propertyDetails = {
        propertyNumber: request?.propertyNumber ?? null,
        offerType: request?.offerType ?? null,
        vendorName: request?.vendorName ?? null
    };

    return {
        state,
        isSubmitting,
        codeStepProps: {
            ...propertyDetails,
            ...getReservationCodeStepView(state, now),
            maskedEmail: maskEmail(userProfile?.email),
            digits: codeEntry.digits,
            focusRequestId: codeEntry.focusRequestId,
            isNewCodeSent: codeEntry.isNewCodeSent,
            isSubmitting,
            nameForm: isProfileNameFailure(state)
                ? {
                      initialName: nameForm.savedName ?? userProfile?.name ?? "",
                      isSaving: nameForm.isSaving,
                      errorMessage: nameForm.errorMessage,
                      onSubmit: engine.submitName
                  }
                : null,
            onDigitsChange: engine.setDigits,
            onComplete: engine.submitCode,
            onResend: engine.resend,
            onRetry: engine.retry
        },
        confirmedStepProps: {
            ...propertyDetails,
            variant: CONFIRMED_VARIANTS[request ? getReservationEntry(request) : "cta"]
        },
        start: engine.start,
        reset: engine.reset
    };
};

export const useReservationFlow = (params: IUseReservationFlowParams): IUseReservationFlowResult => {
    const {request, authPlacement, amplitudeLocation, autoStart = false, isVisible = true, onUnauthenticated} = params;

    const dispatch = useAppDispatch();
    const [postInitiate] = usePostReservationInitiateMutation();
    const [postConfirm] = usePostReservationConfirmMutation();
    const [patchUserProfile] = usePatchUserProfileSilentlyMutation();
    const {data: userProfile} = useGetUserProfileQuery();

    const [state, stateRef, setState] = useRefState(reservationFlowInitialState);
    const codeEntry = useReservationCodeEntry(isVisible);
    const [nameForm, nameFormActions] = useReservationNameForm();
    const tracking = useReservationFlowTracking({request, state, isVisible, authPlacement, amplitudeLocation});
    const clock = useReservationClock(state);
    const {refresh: refreshClock} = clock;
    const {actions: codeEntryActions} = codeEntry;

    const runRef = useRef(createReservationFlowRun());
    const latestRef = useRef<IReservationFlowLatestParams>({request, onUnauthenticated, postInitiate, postConfirm, patchUserProfile});
    const isMountedRef = useIsMountedRef();

    useEffect(() => {
        latestRef.current = {request, onUnauthenticated, postInitiate, postConfirm, patchUserProfile};
    });

    // Created once - it reads params through refs and uses stable setters only
    const engine = useMemo(() => {
        const transitions = createReservationFlowTransitions({stateRef, setState, refreshClock, setIsNewCodeSent: codeEntryActions.setIsNewCodeSent});

        return createReservationFlowEngine({
            dispatch,
            stateRef,
            runRef,
            latestRef,
            isMountedRef,
            transitions,
            codeEntry: codeEntryActions,
            tracking,
            nameFormActions
        });
    }, [dispatch, stateRef, setState, refreshClock, codeEntryActions, isMountedRef, tracking, nameFormActions]);

    const requestPropertyId = request?.propertyId ?? null;

    useEffect(() => {
        if (autoStart && requestPropertyId !== null) {
            engine.start();
        }
    }, [autoStart, requestPropertyId, engine]);

    useHandleExpiryTimer(state, engine.expireHandle);

    return getReservationFlowResult({request, state, now: clock.now, userProfile, codeEntry, nameForm, engine});
};
